// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/DimensionValue.yaml
// Generated: 2026-05-31T18:49:09.410Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { dimensionValueService } from '@/services/finance/DimensionValueService';
import { CreateDimensionValue, UpdateDimensionValue, DimensionValue } from '@/types/finance/DimensionValue';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for DimensionValue
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const dimensionvalueKeys = {
  all: (tenantId?: string | null) => ['dimensionvalues', tenantId] as const,
  lists: (tenantId?: string | null) => [...dimensionvalueKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...dimensionvalueKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...dimensionvalueKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...dimensionvalueKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all DimensionValues
 */
export function useDimensionValues(options?: Omit<UseQueryOptions<DimensionValue[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<DimensionValue[], Error>({
    queryKey: dimensionvalueKeys.lists(tenantId),
    queryFn: () => dimensionValueService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single DimensionValue by ID
 */
export function useDimensionValue(
  id: string,
  options?: Omit<UseQueryOptions<DimensionValue | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<DimensionValue | undefined, Error>({
    queryKey: dimensionvalueKeys.detail(id, tenantId),
    queryFn: () => dimensionValueService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new DimensionValue
 */
export function useCreateDimensionValue() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<DimensionValue, Error, CreateDimensionValue, { previousDimensionValues?: DimensionValue[] }>({
    mutationFn: (data: CreateDimensionValue) => dimensionValueService.create(data),
    onMutate: async (newDimensionValue) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: dimensionvalueKeys.lists(tenantId) });

      // Snapshot previous value
      const previousDimensionValues = queryClient.getQueryData<DimensionValue[]>(dimensionvalueKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<DimensionValue[]>(dimensionvalueKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newDimensionValue, id: (newDimensionValue as { id?: string }).id ?? 'temp-' + Date.now() } as DimensionValue
      ]);

      return { previousDimensionValues };
    },
    onError: (_err, _newDimensionValue, context) => {
      // Rollback on error
      if (context?.previousDimensionValues) {
        queryClient.setQueryData(dimensionvalueKeys.lists(tenantId), context.previousDimensionValues);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: dimensionvalueKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing DimensionValue
 */
export function useUpdateDimensionValue() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<DimensionValue, Error, { id: string; data: UpdateDimensionValue }, { previousDimensionValue?: DimensionValue; previousDimensionValues?: DimensionValue[] }>({
    mutationFn: ({ id, data }) => dimensionValueService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: dimensionvalueKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: dimensionvalueKeys.lists(tenantId) });

      // Snapshot previous values
      const previousDimensionValue = queryClient.getQueryData<DimensionValue>(dimensionvalueKeys.detail(id, tenantId));
      const previousDimensionValues = queryClient.getQueryData<DimensionValue[]>(dimensionvalueKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<DimensionValue>(dimensionvalueKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as DimensionValue);

      // Optimistically update list
      queryClient.setQueryData<DimensionValue[]>(dimensionvalueKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as DimensionValue : item))
      );

      return { previousDimensionValue, previousDimensionValues };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousDimensionValue) {
        queryClient.setQueryData(dimensionvalueKeys.detail(id, tenantId), context.previousDimensionValue);
      }
      if (context?.previousDimensionValues) {
        queryClient.setQueryData(dimensionvalueKeys.lists(tenantId), context.previousDimensionValues);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: dimensionvalueKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: dimensionvalueKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a DimensionValue
 */
export function useDeleteDimensionValue() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousDimensionValues?: DimensionValue[] }>({
    mutationFn: (id: string) => dimensionValueService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: dimensionvalueKeys.lists(tenantId) });

      // Snapshot previous value
      const previousDimensionValues = queryClient.getQueryData<DimensionValue[]>(dimensionvalueKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<DimensionValue[]>(dimensionvalueKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousDimensionValues };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousDimensionValues) {
        queryClient.setQueryData(dimensionvalueKeys.lists(tenantId), context.previousDimensionValues);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: dimensionvalueKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveDimensionValue() {
  const createMutation = useCreateDimensionValue();
  const updateMutation = useUpdateDimensionValue();

  return {
    mutate: (params: { id?: string; data: CreateDimensionValue | UpdateDimensionValue }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateDimensionValue }, options);
      } else {
        return createMutation.mutate(params.data as CreateDimensionValue, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
